import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import ProfileDataContext from '@/context/ProfileDataContext';
import useTranslations from '@/i18n/useTranslations';
import GalleryService from '@/services/gallery.service';
import gallerySubscriptionService from '@/services/gallerySubscription.service';
import UserService from '@/services/user.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import Text from '@/shared-components/text/components/Text';

import './styles/BankAccountForm.css';

function normalizeIdentifier(value) {
  return typeof value === "string" ? value.trim().toUpperCase().replace(/\s+/g, "") : "";
}

function getSuggestedVatId({ businessId, countryIso }) {
  if (countryIso === "ES" && /^[A-Z]\d{7}[A-Z0-9]$/.test(businessId)) {
    return `ES${businessId}`;
  }
  return "";
}

export default function BankAccountForm({ fetchData, setFetchData, galleryBusinessId }) {
  const { profileData, setProfileData } = useContext(ProfileDataContext);
  const t = useTranslations('common.user_settings.bank_account.form');
  const [formState, setFormState] = useState({ ...fetchData, one_stop_shop: fetchData.one_stop_shop });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState(null);
  const syncedStripeAccountIdRef = useRef(null);
  const syncedVatTaxIdRef = useRef(null);
  const [connectCountries, setConnectCountries] = useState([]);
  const [connectCountriesLoading, setConnectCountriesLoading] = useState(false);
  const [connectCountriesError, setConnectCountriesError] = useState(null);
  const [showRequirements, setShowRequirements] = useState(false);
  const [vatId, setVatId] = useState("");
  const [vatError, setVatError] = useState(null);
  const [vatVerificationPending, setVatVerificationPending] = useState(false);
  const [vatLoading, setVatLoading] = useState(false);
  const [vatConfig, setVatConfig] = useState(null);
  const [, setVatConfigLoading] = useState(false);
  const [vatConfigError, setVatConfigError] = useState(null);

  const stripeConnectMeta = useMemo(() => {
    const profilePayment = profileData?.user_payment ?? {};
    const fetchedPayment = fetchData?.user_payment ?? {};
    const accountId =
      profilePayment?.stripe_connect_account_id ??
      fetchedPayment?.stripe_connect_account_id ??
      fetchData?.stripe_connect_account_id ??
      null;
    const status =
      profilePayment?.stripe_connect_status ??
      fetchedPayment?.stripe_connect_status ??
      fetchData?.stripe_connect_status ??
      null;
    const chargesEnabled =
      profilePayment?.stripe_connect_charges_enabled ??
      fetchedPayment?.stripe_connect_charges_enabled ??
      fetchData?.stripe_connect_charges_enabled ??
      null;
    const payoutsEnabled =
      profilePayment?.stripe_connect_payouts_enabled ??
      fetchedPayment?.stripe_connect_payouts_enabled ??
      fetchData?.stripe_connect_payouts_enabled ??
      null;
    const detailsSubmitted =
      profilePayment?.stripe_connect_details_submitted ??
      fetchedPayment?.stripe_connect_details_submitted ??
      fetchData?.stripe_connect_details_submitted ??
      null;
    const requirements =
      profilePayment?.stripe_connect_requirements ??
      fetchedPayment?.stripe_connect_requirements ??
      fetchData?.stripe_connect_requirements ??
      null;
    const disabledReason =
      profilePayment?.stripe_connect_disabled_reason ??
      fetchedPayment?.stripe_connect_disabled_reason ??
      fetchData?.stripe_connect_disabled_reason ??
      null;
    return {
      accountId,
      status: typeof status === "string" ? status.trim().toLowerCase() : status,
      chargesEnabled,
      payoutsEnabled,
      detailsSubmitted,
      requirements,
      disabledReason,
    };
  }, [fetchData, profileData]);

  const stripeConnectState = useMemo(() => {
    if (!stripeConnectMeta.accountId) {
      return "not_connected";
    }
    const hasPayoutsFlag = typeof stripeConnectMeta.payoutsEnabled === "boolean";
    if (hasPayoutsFlag) {
      return stripeConnectMeta.payoutsEnabled ? "active" : "pending";
    }
    return stripeConnectMeta.status === "active" ? "active" : "pending";
  }, [stripeConnectMeta]);

  const stripeStatusLabel = useMemo(() => {
    if (stripeConnectState === "active") {
      return t('stripe.status.connected');
    }
    return t('stripe.status.not_connected');
  }, [stripeConnectState, t]);

  const stripeCtaLabel = useMemo(() => {
    if (stripeConnectState === "active") {
      return t('stripe.buttons.open_dashboard');
    }
    return t('stripe.buttons.connect_stripe');
  }, [stripeConnectState, t]);

  const stripeRequirements = useMemo(() => {
    const requirements = stripeConnectMeta.requirements ?? {};
    const currentlyDue = Array.isArray(requirements.currently_due)
      ? requirements.currently_due
      : Array.isArray(requirements.currentlyDue)
        ? requirements.currentlyDue
        : [];
    const disabledReason =
      requirements.disabled_reason ??
      requirements.disabledReason ??
      stripeConnectMeta.disabledReason ??
      null;
    return {
      currentlyDue,
      disabledReason: typeof disabledReason === "string" ? disabledReason : null,
    };
  }, [stripeConnectMeta.disabledReason, stripeConnectMeta.requirements]);

  const stripeRequirementItems = useMemo(() => {
    const toLabelSegment = (segment) => {
      if (!segment) {
        return "";
      }
      const normalized = segment.replace(/_/g, " ").toLowerCase();
      if (normalized === "mcc") {
        return "MCC";
      }
      if (normalized === "url") {
        return "URL";
      }
      if (normalized === "ip") {
        return "IP";
      }
      if (normalized === "dob") {
        return "Date of birth";
      }
      if (normalized === "vat") {
        return "VAT";
      }
      if (normalized === "oss") {
        return "OSS";
      }
      if (normalized === "line1") {
        return "Line 1";
      }
      if (normalized === "line2") {
        return "Line 2";
      }
      if (normalized === "tax id") {
        return "Tax ID";
      }
      return normalized
        .split(" ")
        .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : ""))
        .join(" ");
    };

    const formatRequirement = (key) => {
      if (!key) {
        return "";
      }
      const explicit = {
        "business_profile.mcc": "Business category (MCC)",
        "business_profile.url": "Business website URL",
        "external_account": "Payout bank account",
        "tos_acceptance.date": "Terms of service acceptance date",
        "tos_acceptance.ip": "Terms of service acceptance IP",
      };
      if (explicit[key]) {
        return explicit[key];
      }
      return key
        .split(".")
        .filter((segment) => {
          if (!segment) {
            return false;
          }
          const normalized = segment.toLowerCase();
          if (normalized.startsWith("person_")) {
            return false;
          }
          if (/^person_[a-z0-9]+$/i.test(segment)) {
            return false;
          }
          if (/^[a-z0-9]{8,}$/i.test(segment) && normalized.includes("person")) {
            return false;
          }
          return true;
        })
        .map((segment) => toLabelSegment(segment))
        .filter(Boolean)
        .join(" - ");
    };

    return stripeRequirements.currentlyDue.map((item) => ({
      key: item,
      label: formatRequirement(item),
    }));
  }, [stripeRequirements.currentlyDue]);

  const showFinishVerification = useMemo(() => {
    return stripeRequirements.currentlyDue.length > 0 || !!stripeRequirements.disabledReason;
  }, [stripeRequirements]);

  const requirementsCount = stripeRequirements.currentlyDue.length;

  const galleryCountryValue = useMemo(() => {
    const raw =
      profileData?.gallery_address?.country ??
      profileData?.gallery_address?.address_country ??
      "";
    return typeof raw === "string" ? raw.trim() : "";
  }, [profileData]);

  const normalizedGalleryIso = useMemo(() => {
    if (!galleryCountryValue) {
      return "";
    }
    if (galleryCountryValue.length === 2) {
      return galleryCountryValue.toUpperCase();
    }
    const match = connectCountries.find(
      (entry) => String(entry?.name ?? "").trim().toLowerCase() === galleryCountryValue.toLowerCase()
    );
    return match?.isoCode ?? "";
  }, [connectCountries, galleryCountryValue]);

  const isConnectSupported = useMemo(() => {
    if (!galleryCountryValue) {
      return null;
    }
    if (!normalizedGalleryIso) {
      return connectCountries.length > 0 ? false : null;
    }
    return connectCountries.some(
      (entry) => String(entry?.isoCode ?? "").trim().toUpperCase() === normalizedGalleryIso
    );
  }, [connectCountries, galleryCountryValue, normalizedGalleryIso]);

  const isGalleryUser = useMemo(() => {
    const rawType = profileData?.__t ?? profileData?.user_type ?? null;
    return typeof rawType === "string" && rawType.toLowerCase() === "gallery";
  }, [profileData]);

  const resolvedGalleryIso = useMemo(() => {
    if (normalizedGalleryIso) {
      return normalizedGalleryIso;
    }
    if (galleryCountryValue && galleryCountryValue.length === 2) {
      return galleryCountryValue.toUpperCase();
    }
    return "";
  }, [galleryCountryValue, normalizedGalleryIso]);

  const isEuGallery = useMemo(() => {
    const vatRules = vatConfig?.vat_rules ?? vatConfig ?? null;
    const euCountries = Array.isArray(vatRules?.eu_countries)
      ? vatRules.eu_countries
      : (Array.isArray(vatRules?.euCountries) ? vatRules.euCountries : []);
    if (!resolvedGalleryIso || !euCountries.length) {
      return false;
    }
    return euCountries.some((entry) => String(entry).trim().toUpperCase() === resolvedGalleryIso);
  }, [resolvedGalleryIso, vatConfig]);

  const normalizedBusinessId = normalizeIdentifier(galleryBusinessId);
  const normalizedStoredVatId = normalizeIdentifier(profileData?.gallery_tax_id);
  const vatCountryIso = resolvedGalleryIso || (
    galleryCountryValue.toLowerCase() === "spain" ? "ES" : ""
  );
  const suggestedVatId = useMemo(
    () => getSuggestedVatId({ businessId: normalizedBusinessId, countryIso: vatCountryIso }),
    [normalizedBusinessId, vatCountryIso]
  );
  const vatIdCandidate = normalizedStoredVatId && !/^0+$/.test(normalizedStoredVatId)
    ? normalizedStoredVatId
    : suggestedVatId;
  const normalizedVatInput = normalizeIdentifier(vatId);
  const vatVerified = Boolean(
    vatIdCandidate
    && profileData?.gallery_tax_verified === true
    && normalizedStoredVatId === vatIdCandidate
    && normalizedVatInput === normalizedStoredVatId
  );
  const vatPending = !vatVerified && (
    vatVerificationPending
    || (
      profileData?.gallery_tax_verification_status === "pending"
      && normalizedStoredVatId === normalizedVatInput
    )
  );
  const shouldShowVatSection = isGalleryUser && (vatVerified || isEuGallery || vatConfigError);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const shouldRefresh = () => sessionStorage.getItem("stripe_connect_return") === "1";

    const refreshProfile = async () => {
      if (!shouldRefresh()) {
        return;
      }
      try {
        await GalleryService.refreshStripeConnectStatus();
        const { response, data } = await UserService.getUserSession();
        if (response?.ok && data) {
          setProfileData?.(data);
        }
      } finally {
        sessionStorage.removeItem("stripe_connect_return");
        setStripeLoading(false);
      }
    };

    const handleVisibility = () => {
      if (!document.hidden) {
        refreshProfile();
      }
    };

    window.addEventListener("focus", refreshProfile);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("focus", refreshProfile);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [setProfileData]);

  useEffect(() => {
    if (!stripeConnectMeta.accountId) {
      return undefined;
    }
    if (syncedStripeAccountIdRef.current === stripeConnectMeta.accountId) {
      return undefined;
    }

    let active = true;
    syncedStripeAccountIdRef.current = stripeConnectMeta.accountId;
    const refreshStripeMeta = async () => {
      const { response } = await GalleryService.refreshStripeConnectStatus();
      if (!response?.ok || !active) {
        return;
      }
      const sessionResult = await UserService.getUserSession();
      if (active && sessionResult?.response?.ok && sessionResult?.data) {
        setProfileData?.(sessionResult.data);
      }
    };

    refreshStripeMeta();
    return () => {
      active = false;
    };
  }, [setProfileData, stripeConnectMeta.accountId]);

  useEffect(() => {
    if (vatIdCandidate) {
      setVatId(vatIdCandidate);
    }
  }, [vatIdCandidate]);

  useEffect(() => {
    const taxIdSyncKey = profileData?.gallery_tax_id_stripe_id ?? profileData?.gallery_tax_id ?? null;
    if (profileData?.gallery_tax_verification_status !== "pending" || !taxIdSyncKey) {
      return undefined;
    }
    if (syncedVatTaxIdRef.current === taxIdSyncKey) {
      return undefined;
    }

    let active = true;
    syncedVatTaxIdRef.current = taxIdSyncKey;
    const refreshVatStatus = async () => {
      const { response, data } = await gallerySubscriptionService.refreshVatIdStatus();
      if (!response?.ok || !active) {
        return;
      }
      setVatVerificationPending(Boolean(data?.pending));
      const sessionResult = await UserService.getUserSession();
      if (active && sessionResult?.response?.ok && sessionResult?.data) {
        setProfileData?.(sessionResult.data);
      }
    };

    refreshVatStatus();
    return () => {
      active = false;
    };
  }, [profileData?.gallery_tax_id, profileData?.gallery_tax_id_stripe_id, profileData?.gallery_tax_verification_status, setProfileData]);

  useEffect(() => {
    if (!isGalleryUser) {
      setVatConfig(null);
      setVatConfigError(null);
      setVatConfigLoading(false);
      return;
    }
    let active = true;
    setVatConfigLoading(true);
    setVatConfigError(null);
    gallerySubscriptionService.getConfig()
      .then(({ response, data }) => {
        if (!active) {
          return;
        }
        if (response?.ok) {
          setVatConfig(data?.vat_rules ?? null);
          setVatConfigError(null);
        } else {
          setVatConfig(null);
          setVatConfigError(data?.message ?? "Unable to load VAT rules.");
        }
      })
      .catch(() => {
        if (active) {
          setVatConfig(null);
          setVatConfigError("Unable to load VAT rules.");
        }
      })
      .finally(() => {
        if (active) {
          setVatConfigLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [isGalleryUser]);

  useEffect(() => {
    let active = true;
    setConnectCountriesLoading(true);
    setConnectCountriesError(null);
    GalleryService.getStripeConnectCountries()
      .then(({ response, data }) => {
        if (!active) {
          return;
        }
        if (!response?.ok) {
          setConnectCountriesError(data?.message ?? t('stripe.errors.load_countries_failed'));
          setConnectCountries([]);
          return;
        }
        const options = Array.isArray(data?.countries) ? data.countries : [];
        setConnectCountries(options);
      })
      .catch(() => {
        if (active) {
          setConnectCountriesError(t('stripe.errors.load_countries_failed'));
          setConnectCountries([]);
        }
      })
      .finally(() => {
        if (active) {
          setConnectCountriesLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [t]);

  async function updateBankAccount(e) {
    e.preventDefault();
    setLoading(true);
    setError(false);

    const payload = {
      one_stop_shop: Boolean(formState.one_stop_shop),
    };
    if (isConnectSupported === false) {
      payload.bank_name = formState.bank_name || "";
      payload.account_holder_name = formState.account_holder_name || "";
      payload.iban = formState.iban || "";
      payload.swift_code = formState.swift_code || "";
    }

    const { response, data } = await GalleryService.updateBankAccount(payload);

    if(response.ok) {
      setLoading(false); 
      setSuccess(true);
      setFetchData(data);
      setTimeout(()=>setSuccess(false), 3000);
    } else if(response.status === 400) {
      setLoading(false);
      if (data?.error_data?.element) {
        setError({ element: data.error_data.element, error_code: data.error_type });
      } else {
        setError({ message: data?.message ?? t('errors.save_failed') });
      }
    }
  }

  function updateFormState(key, value) {
    setFormState({
      ...formState,
      [key]: value
    });
  }

  function resolveExpirationMs(expiresAt) {
    if (!expiresAt) {
      return null;
    }
    if (typeof expiresAt === "number") {
      return expiresAt > 1e12 ? expiresAt : expiresAt * 1000;
    }
    if (typeof expiresAt === "string") {
      const parsed = Date.parse(expiresAt);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }

  function isExpired(expiresAt) {
    const expiresMs = resolveExpirationMs(expiresAt);
    if (!expiresMs) {
      return false;
    }
    return Date.now() >= expiresMs;
  }

  async function requestStripeOnboardingLink({ allowRetry = true } = {}) {
    const result = await GalleryService.createStripeConnectOnboardingLink();
    if (!result?.response?.ok) {
      return result;
    }
    if (isExpired(result?.data?.expires_at) && allowRetry) {
      return requestStripeOnboardingLink({ allowRetry: false });
    }
    return result;
  }

  async function handleStripeConnect() {
    if (stripeLoading) {
      return;
    }
    setStripeLoading(true);
    setStripeError(null);

    try {
      const { response, data } = await requestStripeOnboardingLink();
      if (!response?.ok) {
        setStripeError(data?.message ?? t('stripe.errors.start_connect_failed'));
        setStripeLoading(false);
        return;
      }

      if (isExpired(data?.expires_at)) {
        setStripeError(t('stripe.errors.link_expired'));
        setStripeLoading(false);
        return;
      }

      if (!data?.url) {
        setStripeError(t('stripe.errors.create_link_failed'));
        setStripeLoading(false);
        return;
      }

      sessionStorage.setItem("stripe_connect_return", "1");
      window.open(data.url, "_blank", "noopener,noreferrer");
      setStripeLoading(false);
    } catch (requestError) {
      setStripeError(t('stripe.errors.start_connect_failed'));
      setStripeLoading(false);
    }
  }

  async function handleStripeDashboard() {
    if (stripeLoading || !stripeConnectMeta.accountId) {
      return;
    }
    setStripeLoading(true);
    setStripeError(null);

    try {
      const { response, data } = await GalleryService.createStripeConnectLoginLink();
      if (!response?.ok) {
        setStripeError(t('stripe.errors.open_dashboard_failed'));
        setStripeLoading(false);
        return;
      }

      if (!data?.url) {
        setStripeError(t('stripe.errors.open_dashboard_failed'));
        setStripeLoading(false);
        return;
      }

      sessionStorage.setItem("stripe_connect_return", "1");
      window.open(data.url, "_blank", "noopener,noreferrer");
      setStripeLoading(false);
    } catch (requestError) {
      setStripeError(t('stripe.errors.open_dashboard_failed'));
      setStripeLoading(false);
    }
  }

  async function handleVerifyVatId() {
    if (vatLoading) {
      return;
    }
    const trimmedVatId = typeof vatId === "string" ? vatId.trim() : "";
    if (!trimmedVatId) {
      setVatError("VAT ID is required.");
      return;
    }
    setVatLoading(true);
    setVatError(null);
    setVatVerificationPending(false);

    const { response, data } = await gallerySubscriptionService.verifyVatId({
      vat_id: trimmedVatId,
      billing_address: profileData?.gallery_address ?? null,
    });

    if (!response?.ok) {
      setVatError(data?.message ?? "VAT ID could not be verified.");
      setVatLoading(false);
      return;
    }

    if (data?.pending) {
      setVatVerificationPending(true);
    }

    if (data?.verified || data?.pending) {
      const sessionResult = await UserService.getUserSession();
      if (sessionResult?.response?.ok && sessionResult?.data) {
        setProfileData?.(sessionResult.data);
      }
    }
    setVatLoading(false);
  }

  return (
    <>
      <div className="bank-account-form__stripe-card">
        <div className="bank-account-form__stripe-content">
          <div className="bank-account-form__stripe-header">
            <div>
              <Text className="bank-account-form__stripe-title">
                {t('stripe.title')}
              </Text>
            </div>
            <span className={`bank-account-form__stripe-status-pill bank-account-form__stripe-status-pill--${stripeConnectState}`}>
              {stripeStatusLabel}
            </span>
          </div>

          <div className="bank-account-form__stripe-section">
            {!connectCountriesLoading && isConnectSupported === false && (
              <Text className="bank-account-form__stripe-error" paragraph>
                {t('stripe.connection_details.not_available')}
              </Text>
            )}
            <ul className="bank-account-form__stripe-checklist">
              {isConnectSupported !== null && (
                <li>
                  <span className={isConnectSupported ? "is-yes" : "is-no"}>
                    {isConnectSupported ? "✓" : "✕"}
                  </span>
                  {t('stripe.connection_details.gallery_country_label')} {galleryCountryValue}
                </li>
              )}
              <li>
                <span className={stripeConnectMeta.chargesEnabled ? "is-yes" : "is-no"}>
                  {stripeConnectMeta.chargesEnabled ? "✓" : "✕"}
                </span>
                {t('stripe.connection_details.checklist.charges_enabled')}
              </li>
              <li>
                <span className={stripeConnectMeta.payoutsEnabled ? "is-yes" : "is-no"}>
                  {stripeConnectMeta.payoutsEnabled ? "✓" : "✕"}
                </span>
                {t('stripe.connection_details.checklist.payouts_enabled')}
              </li>
              <li>
                <span className={stripeConnectMeta.detailsSubmitted ? "is-yes" : "is-no"}>
                  {stripeConnectMeta.detailsSubmitted ? "✓" : "✕"}
                </span>
                {t('stripe.connection_details.checklist.details_submitted')}
              </li>
            </ul>
          </div>

          {(showFinishVerification || requirementsCount > 0 || stripeRequirements.disabledReason || connectCountriesError) && (
            <div className="bank-account-form__stripe-section">
              <Text
                className="bank-account-form__stripe-section-title"
                paragraph>
                {t('stripe.verification.title')}
              </Text>
              {showFinishVerification && (
                <Text className="bank-account-form__stripe-warning" paragraph>
                  {t('stripe.verification.finish_verification')}
                </Text>
              )}
              {requirementsCount > 0 && (
                <div className="bank-account-form__stripe-requirements">
                  <button
                    type="button"
                    className="bank-account-form__stripe-requirements-toggle"
                    onClick={() => setShowRequirements((prev) => !prev)}
                  >
                    {showRequirements ? "Hide requirements" : `Requirements due (${requirementsCount})`}
                  </button>
                  {showRequirements && (
                    <div className="bank-account-form__stripe-requirements-list">
                      {stripeRequirementItems.map((item) => (
                        <span key={item.key} className="bank-account-form__stripe-requirements-chip">
                          {item.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {stripeRequirements.disabledReason && (
                <Text className="bank-account-form__stripe-warning" paragraph>
                  {t('stripe.verification.status_prefix')} {stripeRequirements.disabledReason}
                </Text>
              )}
              {connectCountriesError && (
                <Text className="bank-account-form__stripe-warning" paragraph>
                  {connectCountriesError}
                </Text>
              )}
            </div>
          )}

          {stripeError && (
            <Text className="bank-account-form__stripe-error" paragraph>
              {stripeError}
            </Text>
          )}

          <div className="bank-account-form__stripe-footer">
            <a
              className="bank-account-form__stripe-guide-link"
              href="/profile/gallery-help/payment-and-invoices"
              target="_blank"
              rel="noreferrer"
            >
              {t('stripe.buttons.learn_more')}
            </a>
          </div>
        </div>
        <div className="bank-account-form__stripe-actions">
          <ContinueButton
            type="button"
            loading={stripeLoading}
            disabled={isConnectSupported === false}
            onClick={stripeConnectState === "active" ? handleStripeDashboard : handleStripeConnect}
            className="bank-account-form__stripe-button"
          >
            {stripeCtaLabel}
          </ContinueButton>
        </div>
      </div>
      {shouldShowVatSection && (
        <div className="bank-account-form__stripe-card bank-account-form__vat-card">
          <div className="bank-account-form__stripe-content">
            <div className="bank-account-form__stripe-header">
              <div>
                <Text className="bank-account-form__stripe-title">
                  {t('tax.id_number_title')}
                </Text>
              </div>
              <span
                className={`bank-account-form__stripe-status-pill bank-account-form__stripe-status-pill--${
                  vatVerified ? "active" : (vatError ? "error" : "pending")
                }`}
              >
                {vatVerified
                  ? t('tax.verified')
                  : (vatError ? t('tax.invalid_vat_id') : (vatPending ? t('tax.verification_pending') : t('tax.not_verified')))}
              </span>
            </div>

            <div className="bank-account-form__stripe-section">
              {normalizedBusinessId && (
                <Text paragraph>
                  {t('tax.business_id')}: {normalizedBusinessId}
                </Text>
              )}
              <PublicFormInput
                placeholder={t('tax.vat_id_placeholder')}
                element="gallery_vat_id"
                value={vatId}
                readOnly={vatLoading}
                onChange={(event) => {
                  setVatId(event.target.value);
                  setVatError(null);
                  setVatVerificationPending(false);
                }}
                className="bank-account-form__vat-input"
              />
              {vatError && (
                <Text className="bank-account-form__stripe-error" paragraph>
                  {vatError}
                </Text>
              )}
              {vatConfigError && !vatError && (
                <Text className="bank-account-form__stripe-warning" paragraph>
                  {vatConfigError}
                </Text>
              )}
            </div>
          </div>
          <div className="bank-account-form__stripe-actions">
            <ContinueButton
              type="button"
              loading={vatLoading}
              disabled={!vatId || !vatId.trim()}
              onClick={handleVerifyVatId}
              className="bank-account-form__stripe-button"
            >
              {t('tax.verify_vat_id')}
            </ContinueButton>
          </div>
        </div>
      )}
      <form onSubmit={updateBankAccount} className="bank-account-form">
        {isConnectSupported === false && (
          <>
            <Text className="bank-account-form__bank-note" paragraph>
              {t('bank_fields.note')}
            </Text>
            <div className="bank-account-form__bank-fields">
              <PublicFormInput
                placeholder={t('bank_fields.placeholders.bank_name')}
                element="bank_name"
                value={formState.bank_name ?? ""}
                onChange={(event) => updateFormState("bank_name", event.target.value)}
              />
              <PublicFormInput
                placeholder={t('bank_fields.placeholders.account_holder_name')}
                element="account_holder_name"
                value={formState.account_holder_name ?? ""}
                onChange={(event) => updateFormState("account_holder_name", event.target.value)}
              />
              <PublicFormInput
                placeholder={t('bank_fields.placeholders.iban')}
                element="iban"
                error={error}
                value={formState.iban ?? ""}
                onChange={(event) => updateFormState("iban", event.target.value)}
              />
              <PublicFormInput
                placeholder={t('bank_fields.placeholders.swift')}
                element="swift_code"
                error={error}
                value={formState.swift_code ?? ""}
                onChange={(event) => updateFormState("swift_code", event.target.value)}
              />
            </div>
          </>
        )}
        <div className="bank-account-form__tax-section">
          <Text className="bank-account-form__tax-title" paragraph>
            {t('tax.title')}
          </Text>
          <label className={`bank-account-form__tax-checkbox${(loading || success) ? " is-disabled" : ""}`}>
            <input
              type="checkbox"
              checked={Boolean(formState.one_stop_shop)}
              disabled={loading || success}
              onChange={(event) => updateFormState("one_stop_shop", event.target.checked)}
            />
            <span className="bank-account-form__tax-checkbox-label">
              {t('tax.oss_checkbox')}
            </span>
          </label>
        </div>
        {error?.message && (
          <Text className="bank-account-form__stripe-error" paragraph>
            {error.message}
          </Text>
        )}

        <ContinueButton
          loading={loading}
          success={success}
          successText={t('buttons.information_updated')}
          className="bank-account-form__submit-button"
        >
          {t('buttons.update_information')}
        </ContinueButton>
      </form>
    </>
  );
}
